import { DefaultInput, LocalWrapper } from 'netplayjs';
import { MoonDuelNetplayGame } from '@/netplay/MoonDuelNetplayGame';
import { MoonDuelRollbackWrapper } from '@/netplay/MoonDuelRollbackWrapper';

const ROLLBACK_LOCAL_INPUT_DELAY_MS = 50;
const LOGLEVEL_STORAGE_KEY = 'loglevel';
const LOGLEVEL_RESET_FLAG = 'moon_duel_loglevel_reset_done';
const NOISY_LOG_LEVELS = new Set(['TRACE', 'DEBUG', 'INFO']);

function normalizePersistedNetplayLogLevel(): boolean {
  try {
    const raw = window.localStorage.getItem(LOGLEVEL_STORAGE_KEY);
    const normalized = (raw ?? '').toUpperCase();

    if (!raw) {
      window.localStorage.setItem(LOGLEVEL_STORAGE_KEY, 'WARN');
      return false;
    }

    if (NOISY_LOG_LEVELS.has(normalized)) {
      window.localStorage.setItem(LOGLEVEL_STORAGE_KEY, 'WARN');
      const alreadyReloaded = window.sessionStorage.getItem(LOGLEVEL_RESET_FLAG) === '1';
      if (!alreadyReloaded) {
        window.sessionStorage.setItem(LOGLEVEL_RESET_FLAG, '1');
        window.location.reload();
        return true;
      }
    } else {
      window.sessionStorage.removeItem(LOGLEVEL_RESET_FLAG);
    }
  } catch {
    // Ignore storage access issues and continue with defaults.
  }

  return false;
}

function getModeFromHash(): string {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  return (params.get('mode') ?? '').toLowerCase();
}

function installRollbackInputDelay(
  wrapper: { inputReader: { getInput: () => DefaultInput } },
  delayMs: number
): void {
  const timestep = MoonDuelNetplayGame.timestep;
  const delayedFrames = Math.max(0, Math.round(delayMs / timestep));
  if (delayedFrames === 0) return;

  const originalGetInput = wrapper.inputReader.getInput.bind(wrapper.inputReader);
  const buffer: DefaultInput[] = [];

  wrapper.inputReader.getInput = (): DefaultInput => {
    const current = originalGetInput();
    buffer.push(current);
    // Hide newest local inputs for N frames to trade a bit of responsiveness
    // for fewer visible rollbacks when RTT is unstable.
    if (buffer.length <= delayedFrames) {
      return new DefaultInput();
    }
    const delayed = buffer.shift();
    if (!delayed) return new DefaultInput();
    return delayed;
  };
}

async function main(): Promise<void> {
  if (normalizePersistedNetplayLogLevel()) return;
  const mode = getModeFromHash();

  if (mode === 'local') {
    new LocalWrapper(MoonDuelNetplayGame).start();
    return;
  }

  const wrapper = new MoonDuelRollbackWrapper(MoonDuelNetplayGame);
  installRollbackInputDelay(wrapper, ROLLBACK_LOCAL_INPUT_DELAY_MS);
  await wrapper.start();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    void main();
  });
} else {
  void main();
}
